/**
 * Centralized error handling utilities for ALTO ARMOR
 * 
 * Provides safe error message extraction and formatting across the application.
 * Handles all error types safely: Error objects, strings, Supabase errors, unknown types.
 * 
 * Usage:
 * ```typescript
 * import { getToastErrorMessage, getSafeErrorMessage } from '@/lib/errorUtils';
 * 
 * try {
 *   await apiCall();
 * } catch (error) {
 *   toast.error(getToastErrorMessage(error));
 * }
 * ```
 */

export interface SafeError {
  message: string;
  code?: string;
  details?: string;
  isUserFacing: boolean;
}

/**
 * Extracts a safe, user-facing error message from any error type
 * 
 * @param error - Any error value (Error, string, object, unknown)
 * @returns SafeError object with message and optional metadata
 */
export function getSafeErrorMessage(error: unknown): SafeError {
  // Handle null/undefined
  if (!error) {
    return {
      message: 'An unexpected error occurred',
      isUserFacing: true,
    };
  }

  // Handle Error objects (most common case)
  if (error instanceof Error) {
    return {
      message: error.message || 'An unexpected error occurred',
      code: (error as any).code,
      details: import.meta.env.DEV ? error.stack : undefined,
      isUserFacing: true,
    };
  }

  // Handle Supabase and structured error objects
  if (typeof error === 'object' && error !== null) {
    const err = error as any;
    
    // Supabase error format: { message, code, details, hint, status }
    if (err.message && typeof err.message === 'string') {
      return {
        message: err.message,
        code: err.code || err.status || err.statusCode,
        details: err.details || err.hint,
        isUserFacing: true,
      };
    }
    
    // Generic object with 'error' property
    if (err.error && typeof err.error === 'string') {
      return {
        message: err.error,
        isUserFacing: true,
      };
    }
    
    // Fetch/network error format
    if (err.statusText) {
      return {
        message: err.statusText,
        code: err.status?.toString(),
        isUserFacing: true,
      };
    }
  }

  // Handle string errors (thrown as strings)
  if (typeof error === 'string') {
    return {
      message: error,
      isUserFacing: true,
    };
  }

  // Fallback for completely unknown error types
  // In development, try to show the error; in production, generic message
  return {
    message: 'An unexpected error occurred',
    details: import.meta.env.DEV 
      ? `Unknown error type: ${JSON.stringify(error, null, 2)}` 
      : undefined,
    isUserFacing: true,
  };
}

/**
 * Formats error for toast notifications
 * Ensures error messages are always strings and user-friendly
 * 
 * @param error - Any error value
 * @returns User-safe error message string (never "[object Object]")
 * 
 * @example
 * ```typescript
 * catch (error) {
 *   toast.error(getToastErrorMessage(error));
 * }
 * ```
 */
export function getToastErrorMessage(error: unknown): string {
  const safeError = getSafeErrorMessage(error);
  return safeError.message;
}

/**
 * Formats error for logging (includes more details)
 * Useful for console.error or sending to error tracking services
 * 
 * @param error - Any error value
 * @returns Detailed error message with code and details if available
 * 
 * @example
 * ```typescript
 * catch (error) {
 *   console.error(getLogErrorMessage(error));
 *   // Also send to monitoring service
 * }
 * ```
 */
export function getLogErrorMessage(error: unknown): string {
  const safeError = getSafeErrorMessage(error);
  let message = safeError.message;
  
  if (safeError.code) {
    message += ` (Code: ${safeError.code})`;
  }
  
  if (safeError.details && import.meta.env.DEV) {
    message += `\nDetails: ${safeError.details}`;
  }
  
  return message;
}

/**
 * Determines if an error is a network/connectivity issue
 * Useful for deciding whether to show a retry option
 * 
 * @param error - Any error value
 * @returns true if error appears to be network-related
 * 
 * @example
 * ```typescript
 * if (isNetworkError(error)) {
 *   toast.error('Network error. Please check your connection.', {
 *     action: { label: 'Retry', onClick: retry }
 *   });
 * }
 * ```
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('connection') ||
      message.includes('timeout') ||
      message.includes('offline')
    );
  }
  
  if (typeof error === 'object' && error !== null) {
    const err = error as any;
    return (
      err.code === 'NETWORK_ERROR' ||
      err.code === 'FETCH_ERROR' ||
      err.code === 'ECONNREFUSED' ||
      err.status === 0 ||
      err.name === 'NetworkError'
    );
  }
  
  return false;
}

/**
 * Determines if an error is an authentication issue
 * Useful for deciding whether to redirect to login
 * 
 * @param error - Any error value
 * @returns true if error appears to be auth-related
 * 
 * @example
 * ```typescript
 * if (isAuthError(error)) {
 *   toast.error('Please sign in again');
 *   router.push('/auth');
 * }
 * ```
 */
export function isAuthError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('unauthorized') ||
      message.includes('unauthenticated') ||
      message.includes('authentication') ||
      message.includes('token') ||
      message.includes('session') ||
      message.includes('jwt') ||
      message.includes('expired')
    );
  }
  
  if (typeof error === 'object' && error !== null) {
    const err = error as any;
    return (
      err.code === 'PGRST301' || // Supabase JWT expired
      err.code === 'PGRST302' || // Supabase invalid JWT
      err.code === 'UNAUTHORIZED' ||
      err.status === 401 ||
      err.statusCode === 401 ||
      err.name === 'AuthError'
    );
  }
  
  return false;
}

/**
 * Determines if an error is a permission/authorization issue
 * Useful for showing appropriate messaging
 * 
 * @param error - Any error value
 * @returns true if error appears to be permission-related
 */
export function isPermissionError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('permission') ||
      message.includes('forbidden') ||
      message.includes('access denied') ||
      message.includes('insufficient')
    );
  }
  
  if (typeof error === 'object' && error !== null) {
    const err = error as any;
    return (
      err.code === 'PGRST301' ||
      err.code === 'FORBIDDEN' ||
      err.status === 403 ||
      err.statusCode === 403
    );
  }
  
  return false;
}

/**
 * Determines if an error is a validation issue
 * Useful for showing field-specific error messages
 * 
 * @param error - Any error value
 * @returns true if error appears to be validation-related
 */
export function isValidationError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('validation') ||
      message.includes('invalid') ||
      message.includes('required') ||
      message.includes('must be')
    );
  }
  
  if (typeof error === 'object' && error !== null) {
    const err = error as any;
    return (
      err.code === 'VALIDATION_ERROR' ||
      err.status === 400 ||
      err.statusCode === 400 ||
      err.name === 'ValidationError'
    );
  }
  
  return false;
}
